/**
 * ConfirmDialog (DESIGN.md T8): the AlertDialog pattern as one composite - title names the action
 * and the object, the body states the consequence, the confirm button carries the verb
 * (destructive variant), focus starts on Cancel, and the dialog stays busy while the action runs.
 *
 * High-risk actions use type-to-confirm: with `confirmText` the button only enables once the user
 * typed exactly that text (e.g. the host name) into the field labelled `confirmTextLabel`.
 *
 * Three-way choices (e.g. leaving the composer: Save draft / Discard / Cancel) add an `alternative` action between
 * Cancel and the confirm button; it runs with the same busy state.
 */
import { useId, useRef, useState, type ComponentProps, type ReactNode, type RefObject } from 'react';
import { restoreFocus } from '../lib/focus';
import { useReportEdits } from '../hooks/use-edit-state';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/alert-dialog';
import { Button } from '../components/button';
import { Input } from '../components/input';
import { Label } from '../components/label';
import { useUiStrings } from '../strings';

export interface ConfirmDialogProps {
  returnFocusRef?: RefObject<HTMLElement | null>;
  onCloseAutoFocus?: ComponentProps<typeof AlertDialogContent>['onCloseAutoFocus'];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** The verb, e.g. "Delete pack". */
  confirmLabel: string;
  destructive?: boolean;
  /** Runs while the dialog shows a busy state; the dialog closes when it resolves. */
  onConfirm: () => Promise<void> | void;
  /** Type-to-confirm: the exact text the user must type before the button enables. */
  confirmText?: string;
  /** Label of the type-to-confirm field (names what to type). */
  confirmTextLabel?: string;
  /** Extra content between the description and the buttons (e.g. a warning). */
  children?: ReactNode;
  /** Keeps the confirm button disabled (e.g. until an acknowledgement checkbox in `children` is ticked). */
  confirmDisabled?: boolean;
  /** Pending confirmed operations participate in navigation guards; synchronous draft-discard prompts opt out. */
  reportBusy?: boolean;
  /** A second action next to the confirm button (e.g. "Discard" beside "Save draft"); the dialog closes when it resolves. */
  alternative?: { label: string; onSelect: () => Promise<void> | void; destructive?: boolean };
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive = true,
  onConfirm,
  confirmText,
  confirmTextLabel,
  children,
  confirmDisabled = false,
  reportBusy = true,
  alternative,
  returnFocusRef,
  onCloseAutoFocus,
}: ConfirmDialogProps) {
  const strings = useUiStrings();
  const inputId = useId();
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  useReportEdits(false, open && busy && reportBusy);
  const [typed, setTyped] = useState('');
  const matches = confirmText === undefined || typed.trim() === confirmText;

  const change = (next: boolean) => {
    if (!next && running.current) return;
    if (!next) setTyped('');
    onOpenChange(next);
  };
  /** Runs an action with the busy state; closes when it resolves, stays open when it rejects. */
  const run = (action: () => Promise<void> | void) => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    Promise.resolve()
      .then(action)
      .then(() => { running.current = false; change(false); })
      .catch(() => undefined)
      .finally(() => { running.current = false; setBusy(false); });
  };

  return (
    <AlertDialog open={open} onOpenChange={change}>
      <AlertDialogContent onCloseAutoFocus={(event) => { onCloseAutoFocus?.(event); restoreFocus(event, returnFocusRef); }}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {children}
        {confirmText !== undefined && (
          <div className="flex flex-col gap-2">
            <Label htmlFor={inputId}>{confirmTextLabel ?? confirmText}</Label>
            <Input id={inputId} value={typed} autoComplete="off" spellCheck={false} disabled={busy} onChange={(event) => setTyped(event.target.value)} />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{strings.cancel}</AlertDialogCancel>
          {alternative && (
            <Button variant={alternative.destructive ? 'destructive' : 'outline'} disabled={busy} onClick={() => run(alternative.onSelect)}>
              {alternative.label}
            </Button>
          )}
          {/* A plain Button (not AlertDialogAction): the dialog must stay open until the action settles. */}
          <Button variant={destructive ? 'destructive' : 'default'} disabled={busy || !matches || confirmDisabled} onClick={() => run(onConfirm)}>
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
